import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, IconButton
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { PlayCircle } from 'react-feather';

import { useAuth } from '../contexts/AuthContext';
import { usePrelaunch } from '../contexts/PrelaunchContext';
import usePageTitle from '../hooks/usePageTitle';
import PublicPageLayout from '../components/layout/PublicPageLayout';
import Lightbox from '../components/common/Lightbox';
import CanonicalLink from '../components/common/CanonicalLink';
import { captureUTMParams } from '../utils/analytics';

import LandingInteractiveForm from '../components/landing/LandingInteractiveForm';
import ScrollIndicator from '../components/landing/ScrollIndicator';
import Benefits from '../components/landing/Benefits';
import HowItWorks from '../components/landing/HowItWorks';
import Testimonials from '../components/landing/Testimonials';
import FeaturesSection from '../components/landing/features/FeaturesSection';
import FAQSection from '../components/landing/FAQSection';

import carousel1 from '../assets/images/carousel-1.png';
import carousel2 from '../assets/images/carousel-2.png';
import carousel3 from '../assets/images/carousel-3.png';

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { isPrelaunch } = usePrelaunch();

  // Get campaign from URL parameters for dynamic content
  const params = new URLSearchParams(window.location.search);
  const campaign = params.get('utm_campaign');

  // Campaign-specific page titles
  const pageTitle = campaign === 'wavescoop-ideagen'
    ? 'AI-Powered Content Ideas That Go Viral | WaveScoop'
    : campaign === 'wavescoop-postsim'
    ? 'Test Your Posts Before Publishing | WaveScoop'
    : 'Content Ideas That Outrun the Algorithm | WaveScoop';

  usePageTitle(pageTitle, false);

  const goStart = () => (isAuthenticated ? navigate('/simulations/create') : navigate('/login?source=landing'));

  const carousel = [
    { src: carousel1, alt: 'Idea Generation interface', id: 'c1', caption: 'Draft ideas in seconds' },
    { src: carousel2, alt: 'Sentiment forecast dashboard', id: 'c2', caption: 'Forecast reactions instantly' },
    { src: carousel3, alt: 'Sample reactions thread', id: 'c3', caption: 'Spot potential backlash early' }
  ];

  const [i, setI] = useState(0);
  const [startX, setStartX] = useState<number | null>(null);
  const [endX, setEndX] = useState<number | null>(null);
  const [lightbox, setLightbox] = useState({ open: false, src: '', alt: '' });

  const next = () => setI(p => (p + 1) % carousel.length);
  const prev = () => setI(p => (p - 1 + carousel.length) % carousel.length);
  const go = (n: number) => setI(n);

  // Capture UTM parameters on landing page load
  useEffect(() => {
    captureUTMParams();
  }, []);

  useEffect(() => { const t = setTimeout(next, 7500); return () => clearTimeout(t); }, [i]);

  const onTouchStart = (e: React.TouchEvent) => { setStartX(e.targetTouches[0].clientX); setEndX(null); };
  const onTouchMove = (e: React.TouchEvent) => setEndX(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (startX == null || endX == null) return;
    const d = startX - endX;
    if (d > 50) next();
    else if (d < -50) prev();
    setStartX(null); setEndX(null);
  };

  return (
    <PublicPageLayout>
      <Helmet>
        <meta
          name="description"
          content="Plan social media content with audience-tuned insights and pre-launch checks. Strong ideas, predictable impact, less guesswork."
        />
        <meta property="og:title" content="Content Ideas That Outrun the Algorithm" />
        <meta property="og:description" content="Plan social content with audience-tuned insights and pre-launch checks." />
        <meta name="twitter:title" content="Content Ideas That Outrun the Algorithm" />
        <meta name="twitter:description" content="Confident social planning without the guesswork." />
        <meta name="twitter:card" content="summary_large_image" />
        {isPrelaunch && <script src="https://getlaunchlist.com/js/widget-diy.js" defer></script>}
      </Helmet>
      <CanonicalLink />

      <Box component="main" sx={{ flexGrow: 1, position: 'relative', zIndex: 2 }}>
        {/* HERO - Minimal first view */}
        <Box 
          component="section" 
          sx={{ 
            minHeight: { xs: 'calc(100svh - 80px)', md: 'calc(100vh - 80px)' }, // Use small viewport height on mobile
            display: 'flex',
            flexDirection: 'column',
            justifyContent: { xs: 'flex-start', md: 'center' },
            alignItems: 'center',
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2, md: 4 },
            position: 'relative',
            mt: 0, // Avoid header overlap on mobile
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', sm: '550px', md: '650px' },
              mx: 'auto',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pt: { xs: 1, md: 0 },
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: 'clamp(1.25rem, 4.5vw, 2rem)', md: 'clamp(2.2rem, 3vw, 3rem)' },
                fontWeight: 700,
                lineHeight: 1.15,
                mb: { xs: 1.2, md: 2 },
                color: 'white',
              }}
            >
              Content Ideas that <br />Outrun the Algorithm
            </Typography>

            <Typography
              variant="h5"
              sx={{
                maxWidth: 600,
                mx: 'auto',
                mb: { xs: 1.5, md: 3 },
                opacity: 0.85,
                fontSize: { xs: '0.85rem', md: '1.05rem' },
                lineHeight: 1.5,
                color: 'rgba(255, 255, 255, 0.9)',
              }}
            >
              Plan scroll-stopping social media content <br />with audience-tuned insights and pre-launch checks.
            </Typography>

            <Box sx={{ width: '100%' }}>
              <LandingInteractiveForm />
            </Box>
            
            <ScrollIndicator />
          </Box>
        </Box>

        {/* FEATURES - Directly below the fold */}
        <Box id="features" sx={{ scrollMarginTop: '100px', pb: { xs: 4, md: 6 } }}>
          <FeaturesSection />
        </Box>

        {/* TESTIMONIALS - After Features with spacing */}
        <Box sx={{ pt: { xs: 4, md: 6 } }}>
          <Testimonials />
        </Box>

        {/* FAQ - After Testimonials */}
        <FAQSection
          title="Frequently Asked Questions"
          subtitle="Everything you need to know about creating content that connects"
          questions={[
            {
              question: "How does WaveScoop predict audience reactions?",
              answer: "WaveScoop uses advanced AI models trained on millions of social media interactions to understand sentiment patterns, engagement triggers, and audience psychology. When you input your content, our AI analyzes it against these patterns to predict how different audience segments will likely react, providing you with sentiment scores, engagement estimates, and specific feedback predictions."
            },
            {
              question: "What social media platforms does WaveScoop support?",
              answer: "WaveScoop currently supports content optimization for X (Twitter), Instagram, LinkedIn, TikTok, LinkedIn, and Reddit. Each platform has unique audience behaviors and content preferences, and our AI adapts its predictions based on platform-specific patterns. We're continuously expanding our platform coverage based on user feedback."
            },
            {
              question: "How accurate are the sentiment predictions?",
              answer: "Our predictions typically achieve 85-92% accuracy when compared to actual post performance. The accuracy depends on factors like the amount of historical data available for your brand voice and audience. The more you use WaveScoop, the better it learns your specific audience patterns, improving prediction accuracy over time."
            },
            {
              question: "Can I test different content variations?",
              answer: "Yes! WaveScoop's simulation feature allows you to test multiple versions of your content before publishing. You can experiment with different tones, messaging angles, hashtags, and even images to see which variation resonates best with your target audience. This A/B testing capability helps you optimize content before it goes live."
            },
            {
              question: "How does the AI understand my brand voice?",
              answer: "When you create content in WaveScoop, you can specify your brand's name, industry, and target audience characteristics. The AI uses this context to tailor its predictions and suggestions to match your brand's unique voice. Over time, it learns from your content patterns to provide increasingly personalized recommendations."
            },
            {
              question: "What's the difference between Ideas and Simulations?",
              answer: "Ideas is our AI-powered content generation tool that helps you create on-brand post drafts based on your input and objectives. Simulations, on the other hand, takes any content (whether AI-generated or your own) and predicts how your audience will react to it. Think of Ideas as your creative assistant and Simulations as your pre-launch testing ground."
            },
            {
              question: "Can I export or share the prediction results?",
              answer: "Yes, you can export detailed prediction reports as PDFs to share with your team or clients. These reports include sentiment analysis, engagement forecasts, and specific feedback predictions. You can also share live links to simulation results with team members who have WaveScoop accounts."
            }
          ]}
        />

          {/* BOTTOM CTA */}
        <Box component="section" sx={{ textAlign: 'center', mt: 4, mb: 12, px: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
            Ready to create and predict before you publish?
          </Typography>
          <Typography sx={{ maxWidth: 700, mx: 'auto', mb: 2, opacity: 0.9 }}>
            Start for free and see which draft wins before your audience does.
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
            No credit card required • Cancel anytime
          </Typography>
          <Button variant="contained" size="large" onClick={goStart} sx={{ borderRadius: 28, px: 5, py: 1.8 }}>
            Start free trial
          </Button>
        </Box>
      </Box>

    </PublicPageLayout>
  );
}
